import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const POLL_MS = 2500;
const PING_MS = 25000;
const MAX_BACKOFF_MS = 10000;
const FATAL_CODES = { 4401: "unauthorized", 4404: "not-found", 4409: "full", 4410: "ended" };
const CLOCK_BURST = 5;
const CLOCK_EVERY_MS = 10000;
const CLOCK_SAMPLES = 10;
export const nowMs = () => performance.timeOrigin + performance.now();

const wsUrl = (token) =>
  `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/watch-party/${token}/ws`;

// Live party chat: WebSocket push with auto-reconnect (exponential backoff),
// catch-up fetch on every (re)connect, and 2.5s polling while disconnected.
export default function useWatchPartyChat(token, enabled) {
  const [msgs, setMsgs] = useState([]);
  const [status, setStatus] = useState("connecting");
  const [watching, setWatching] = useState(null);
  const [ended, setEnded] = useState(false);
  const [lastReaction, setLastReaction] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [hostOnline, setHostOnline] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const wsRef = useRef(null);
  // NTP-style clock sync: offset = server clock − local clock, from the lowest-RTT recent probe.
  const clockRef = useRef({ samples: [], offset: null, rtt: null });
  const [rtt, setRtt] = useState(null);
  const lastSeenRef = useRef(null);
  const seenRef = useRef(new Set());

  const merge = useCallback((incoming, { live = false } = {}) => {
    const fresh = incoming.filter((m) => m?.id && !seenRef.current.has(m.id));
    if (!fresh.length) return;
    fresh.forEach((m) => seenRef.current.add(m.id));
    setMsgs((prev) => [...prev, ...fresh].sort((a, b) => (a.created_at < b.created_at ? -1 : 1)));
    const newest = fresh.reduce((acc, m) => (m.created_at > acc ? m.created_at : acc), lastSeenRef.current || "");
    lastSeenRef.current = newest;
    if (live) {
      const r = fresh.filter((m) => m.kind === "reaction").pop();
      if (r) setLastReaction({ ...r, key: `${r.id}-${Date.now()}` });
    }
  }, []);

  const catchUp = useCallback(async () => {
    try {
      const since = lastSeenRef.current;
      const { data } = await api.get(`/watch-party/${token}/chat${since ? `?since=${encodeURIComponent(since)}` : ""}`);
      merge(data?.messages || []);
    } catch (e) {
      if (e?.response?.status === 410) setEnded("host"); // host ended it while we were polling
    }
  }, [token, merge]);

  useEffect(() => {
    if (!enabled) return undefined;
    let ws = null;
    let stopped = false;
    let attempt = 0;
    let reconnectTimer = null;
    let pollTimer = null;
    let pingTimer = null;
    let clockTimers = [];
    const probe = () => { if (ws?.readyState === 1) ws.send(JSON.stringify({ type: "clock", t0: nowMs() })); };
    const stopClock = () => { clockTimers.forEach((t) => { clearTimeout(t); clearInterval(t); }); clockTimers = []; };

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(catchUp, POLL_MS);
    };
    const stopPolling = () => { clearInterval(pollTimer); pollTimer = null; };

    const connect = () => {
      if (stopped) return;
      try {
        ws = new WebSocket(wsUrl(token));
        wsRef.current = ws;
      } catch {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        attempt = 0;
        setStatus("live");
        stopPolling();
        catchUp();
        clearInterval(pingTimer);
        pingTimer = setInterval(() => { if (ws?.readyState === 1) ws.send("ping"); }, PING_MS);
        stopClock();
        clockRef.current = { samples: [], offset: null, rtt: null };
        for (let i = 0; i < CLOCK_BURST; i += 1) clockTimers.push(setTimeout(probe, i * 150));
        clockTimers.push(setInterval(probe, CLOCK_EVERY_MS));
      };
      ws.onmessage = (ev) => {
        if (ev.data === "pong") return;
        let data;
        try { data = JSON.parse(ev.data); } catch { return; }
        if (data.type === "message") merge([data.message], { live: true });
        else if (data.type === "presence") { setWatching(data.count); if (data.host_online != null) setHostOnline(data.host_online); }
        else if (data.type === "hello") setIsHost(!!data.is_host);
        else if (data.type === "sync") setLastSync({ ...data, receivedAt: nowMs() });
        else if (data.type === "clock") {
          const t1 = nowMs();
          const sample = { rtt: t1 - data.t0, offset: data.ts + (t1 - data.t0) / 2 - t1 };
          const c = clockRef.current;
          c.samples = [...c.samples, sample].slice(-CLOCK_SAMPLES);
          const best = c.samples.reduce((a, b) => (b.rtt < a.rtt ? b : a));
          c.offset = best.offset;
          c.rtt = best.rtt;
          setRtt(Math.round(best.rtt));
        }
        else if (data.type === "ended") setEnded(data.by_host === false ? "expired" : "host");
      };
      ws.onclose = (ev) => {
        clearInterval(pingTimer);
        stopClock();
        if (stopped) return;
        if (FATAL_CODES[ev.code]) {
          stopPolling();
          if (ev.code === 4410) setEnded((prev) => prev || "host");
          setStatus(FATAL_CODES[ev.code]);
          return;
        }
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      setStatus("reconnecting");
      startPolling();
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);
      attempt += 1;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, delay);
    };

    const onOnline = () => { if (ws?.readyState !== 1) { attempt = 0; clearTimeout(reconnectTimer); connect(); } };
    window.addEventListener("online", onOnline);
    catchUp();
    connect();
    return () => {
      stopped = true;
      window.removeEventListener("online", onOnline);
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      stopClock();
      stopPolling();
      ws?.close();
    };
  }, [token, enabled, catchUp, merge]);

  const sendSync = useCallback((state) => {
    const ws = wsRef.current;
    if (ws?.readyState === 1) ws.send(JSON.stringify({ type: "sync", ...state }));
  }, []);

  // Server-clock "now" in ms, or null until the first clock probe returns.
  const serverNow = useCallback(() => (clockRef.current.offset == null ? null : nowMs() + clockRef.current.offset), []);

  return { msgs, merge, status, watching, ended, lastReaction, isHost, hostOnline, lastSync, sendSync, serverNow, rtt };
}
